/**
 * Axios Interceptors
 * Request/Response interceptors for authentication
 */

import { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { notification } from 'antd';
import { axiosInstance } from './axios.config';
import { STORAGE_KEYS } from '../constants';
import { AUTH_ENDPOINTS } from '../constants/api.constants';
import { AuthService } from '../services/auth.service';
import { PATHS } from '../routes/constants/paths';

/**
 * Get token from localStorage
 */
const getToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
};

/**
 * Get refresh token from localStorage
 */
const getRefreshToken = (): string | null => {
  return localStorage.getItem('refreshToken');
};

/**
 * Set tokens in localStorage
 */
const setTokens = (accessToken: string, refreshToken: string): void => {
  localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, accessToken);
  localStorage.setItem('refreshToken', refreshToken);
};

// Flag to prevent multiple token refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

/**
 * Process queued requests after token refresh
 */
const processQueue = (error: any = null, token: string | null = null): void => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Helper function to show error notification
 */
const showErrorNotification = (title: string, message: string, duration = 5) => {
  notification.error({
    message: title,
    description: message,
    duration,
    placement: 'bottomRight',
  });
};

/**
 * Clear auth state and redirect to login (used on 401 when refresh fails or unavailable)
 */
const forceLogout = (): void => {
  AuthService.clearAuth();
  window.location.href = '/login';
};

/**
 * Helper function to extract error message from API response
 */
const getErrorMessage = (error: AxiosError): string => {
  const responseData = error.response?.data as any;
  
  // Try to get message from various possible locations
  if (responseData?.message) {
    return responseData.message;
  }
  
  if (responseData?.error) {
    return typeof responseData.error === 'string' ? responseData.error : responseData.error.message;
  }
  
  if (error.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred';
};

/**
 * Request Interceptor
 * Automatically adds Bearer token to all requests
 */
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Optional debug logging for documents upload endpoint (controlled by env flag)
    if (config.url?.includes('documents-upload') && process.env.REACT_APP_ENABLE_LOGGING === 'true') {
      // eslint-disable-next-line no-console
      console.log('🔍 Axios Interceptor - Request config:', {
        url: config.url,
        method: config.method,
        data: config.data,
        headers: config.headers,
      });
      // eslint-disable-next-line no-console
      console.log('🔍 Axios Interceptor - Request body:', config.data);
      // eslint-disable-next-line no-console
      console.log('🔍 Axios Interceptor - Request body type:', typeof config.data);
      // eslint-disable-next-line no-console
      console.log('🔍 Axios Interceptor - Request body JSON:', JSON.stringify(config.data, null, 2));
    }
    
    return config;
  },
  (error: AxiosError) => {
    // Show notification for request errors
    showErrorNotification(
      'Request Error',
      'Failed to send request. Please check your connection.'
    );
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handles token expiration and automatic refresh
 */
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // Successfully received response
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle network errors (no response from server)
    if (!error.response) {
      showErrorNotification(
        'Network Error',
        'Unable to connect to the server. Please check your internet connection and try again.'
      );
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const errorMessage = getErrorMessage(error);

    // Check if error is 401 Unauthorized
    if (status === 401) {
      // Check for specific "Invalid or expired token" message
      const errorMsg = (error.response?.data as any)?.message || '';
      const msg = errorMsg.toLowerCase();
      const isInvalidToken =
        ((msg.includes('invalid') && msg.includes('token')) ||
          (msg.includes('expired') && msg.includes('token')) ||
          errorMsg === 'Invalid or expired token');

      // If token is explicitly invalid/expired, clear auth and redirect to login
      if (isInvalidToken) {
        forceLogout();
        return Promise.reject(error);
      }

      // Don't retry for login, logout, or refresh token endpoints
      const isAuthEndpoint = 
        originalRequest.url?.includes(AUTH_ENDPOINTS.LOGIN) ||
        originalRequest.url?.includes(AUTH_ENDPOINTS.LOGOUT) ||
        originalRequest.url?.includes(AUTH_ENDPOINTS.REFRESH_TOKEN);

      if (isAuthEndpoint) {
        // Show notification for login failures
        if (originalRequest.url?.includes(AUTH_ENDPOINTS.LOGIN)) {
          showErrorNotification(
            'Login Failed',
            errorMessage || 'Invalid credentials. Please check your email and password.'
          );
        }
        return Promise.reject(error);
      }

      // If already retried (refresh failed or didn't help), force logout
      if (originalRequest._retry) {
        forceLogout();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // If refresh is already in progress, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      // Try to refresh token
      const refreshToken = getRefreshToken();
      
      if (!refreshToken) {
        forceLogout();
        return Promise.reject(error);
      }

      isRefreshing = true;

      try {
        // Attempt to refresh the access token
        const response = await axiosInstance.post(AUTH_ENDPOINTS.REFRESH_TOKEN, {
          refreshToken,
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data;

        // Update tokens
        setTokens(accessToken, newRefreshToken);

        // Update authorization header
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        // Process queued requests
        processQueue(null, accessToken);

        // Retry original request
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        forceLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other HTTP error statuses
    switch (status) {
      case 400:
        showErrorNotification(
          'Bad Request',
          errorMessage || 'Invalid request. Please check your input and try again.'
        );
        break;
      
      case 403:
        if ((error.response?.data as any)?.code === 'PASSWORD_RESET_REQUIRED') {
          window.location.assign(PATHS.RESET_PASSWORD);
          break;
        }
        if ((error.response?.data as any)?.code === 'EMAIL_NOT_VERIFIED') {
          window.location.assign(PATHS.VERIFY_EMAIL);
          break;
        }
        if ((error.response?.data as any)?.code === 'ACCOUNT_DISABLED') {
          showErrorNotification('Account Disabled', 'Your account has been disabled. Please contact support.');
          break;
        }
        showErrorNotification(
          'Access Denied',
          errorMessage || 'You do not have permission to perform this action.'
        );
        break;
      
      case 404:
        showErrorNotification(
          'Not Found',
          errorMessage || 'The requested resource was not found.'
        );
        break;
      
      case 409:
        showErrorNotification(
          'Conflict',
          errorMessage || 'A conflict occurred. The resource may already exist.'
        );
        break;
      
      case 422:
        showErrorNotification(
          'Validation Error',
          errorMessage || 'Please check your input and try again.'
        );
        break;
      
      case 429:
        showErrorNotification(
          'Too Many Requests',
          errorMessage || 'You have made too many requests. Please try again later.'
        );
        break;
      
      case 500:
        showErrorNotification(
          'Server Error',
          errorMessage || 'An internal server error occurred. Please try again later.'
        );
        break;
      
      case 502:
        showErrorNotification(
          'Bad Gateway',
          errorMessage || 'The server is temporarily unavailable. Please try again later.'
        );
        break;
      
      case 503:
        showErrorNotification(
          'Service Unavailable',
          errorMessage || 'The service is temporarily unavailable. Please try again later.'
        );
        break;
      
      case 504:
        showErrorNotification(
          'Gateway Timeout',
          errorMessage || 'The request took too long to complete. Please try again.'
        );
        break;
      
      default:
        // For any other error status codes
        if (status && status >= 400) {
          showErrorNotification(
            `Error ${status}`,
            errorMessage || 'An error occurred while processing your request.'
          );
        }
        break;
    }

    // For other errors, reject normally
    return Promise.reject(error);
  }
);

export default axiosInstance;

